import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';

import { HeapEvent, ErrorMessage } from 'config';
import { hasTouchScreen } from 'utils/device';

import type { ReducedCartItem } from 'models/CartItem';
import CartProduct from 'models/CartProduct';

import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useCart } from '../../../contexts/Cart';

type Props = {
  cartProduct: CartProduct;
  areServingsUpdatable: boolean;
  label?: string;
  tooltipErrorMessage?: string;
  hideLabel?: boolean;
  showTooltip?: boolean;
  onChange: (skuIds: ReducedCartItem[]) => void;
  heapEvents: {
    twoServings: HeapEvent,
    fourServings: HeapEvent
  };
  onRecipePage?: boolean;
};

function ServingsToggle({
  cartProduct,
  areServingsUpdatable,
  label = 'Servings',
  tooltipErrorMessage = '',
  hideLabel = false,
  showTooltip = false,
  onChange,
  heapEvents,
  onRecipePage = false,
}: Props): ReactElement {
  const {
    currentItem,
    servingOptions,
    highestServingItem,
    lowestServingItem,
    hasServingOptions,
    isPartiallySoldOut,
  } = cartProduct;
  const { cart } = useCart();
  const areServingsDecreasable = useRef(true);
  useEffect(() => {
    if (hasServingOptions && highestServingItem.isSelected) {
      const subtotalAfterDecrease = cart.subtotalPrice
        - highestServingItem.unitPrice
        + lowestServingItem.unitPrice;
      areServingsDecreasable.current = subtotalAfterDecrease >= cart.validations.minimum_base_price;
    }
  }, [cart, highestServingItem, lowestServingItem, hasServingOptions]);
  const { servings, title, id: skuId } = currentItem.sku;
  const [isTooltipVisible, setTooltipVisible] = useState(false);
  if (onRecipePage && !areServingsDecreasable) setTooltipVisible(true);

  const toggleTooltipVisibility = (show?) => {
    if (showTooltip && (
      !areServingsUpdatable
        || isPartiallySoldOut
        || tooltipErrorMessage
    )) {
      if (show === undefined) {
        setTooltipVisible(!isTooltipVisible);
      } else if (!hasTouchScreen) {
        setTooltipVisible(show);
      }
    }
  };

  const triggerChange = (srv) => {
    onChange(cartProduct.items.map(
      (item) => (
        item.sku.servings === srv
          ? { skuId: item.sku.id, quantity: 1 }
          : { skuId: item.sku.id, quantity: 0 }
      ),
    ));
  };

  return (
    <>
      <div className="c-add-to-cart__servings">
        {!hideLabel && (
          <div className="c-add-to-cart__servings-title">
            {label}
          </div>
        )}
        <OverlayTrigger
          show={isTooltipVisible}
          overlay={(
            <Tooltip id="tooltip">
              <p>
                {tooltipErrorMessage || (
                  isPartiallySoldOut
                    ? ErrorMessage.SOLD_OUT_SERVING
                    : ErrorMessage.MAX_BOX
                )}
              </p>
            </Tooltip>
          )}
        >
          <div
            className="c-add-to-cart__servings-toggle list-group"
            data-toggle="tooltio"
            role="radiogroup"
            aria-label="Servings selector"
            onTouchEnd={() => toggleTooltipVisibility()}
            onPointerEnter={() => toggleTooltipVisibility(true)}
            onPointerLeave={() => toggleTooltipVisibility(false)}
          >
            <button
              id={`toggle-servings-${servingOptions[0]}-${skuId}`}
              type="button"
              disabled={
                (onRecipePage && !areServingsDecreasable.current)
                || (isPartiallySoldOut && !(servings === servingOptions[0]))
              }
              onClick={() => triggerChange(servingOptions[0])}
              className={[
                heapEvents.twoServings,
                servings === servingOptions[0] ? 'is-active' : '',
              ].join(' ')}
              role="radio"
              aria-checked={servings === servingOptions[0]}
            >
              <span className="sr-only">Change to</span>
            &nbsp;
              {servingOptions[0]}
            &nbsp;
              <span className="sr-only">
                {`servings for ${title}`}
              </span>
            </button>

            <button
              id={`toggle-servings-${servingOptions[1]}-${skuId}`}
              type="button"
              disabled={
              !areServingsUpdatable
              || (isPartiallySoldOut && !(servings === servingOptions[1]))
            }
              onClick={() => triggerChange(servingOptions[1])}
              className={[
                heapEvents.fourServings,
                servings === servingOptions[1] ? 'is-active' : '',
              ].join(' ')}
              role="radio"
              aria-checked={servings === servingOptions[1]}
            >
              {servingOptions[1]}
            &nbsp;
              <span className="sr-only">
                {`servings for ${title} selected`}
              </span>
            </button>
          </div>
        </OverlayTrigger>
      </div>
    </>
  );
}

export default ServingsToggle;
